/**
 * Saúde — os nove alarmes, com o que fazer ao lado.
 *
 * A frase que justifica esta tela está no docs/ARCHITECTURE.md e vale repetir:
 * **um publicador que falha em silêncio é pior que nenhum publicador**, porque
 * a marca acha que está publicando.
 *
 * Cada alarme aponta para a seção correspondente do docs/RUNBOOK-ALARMES.md.
 * O código do alarme é o mesmo nos dois lugares, de propósito: quem for
 * acordado às 3h procura pelo código, não pela frase.
 */
import { Api, type Alarme } from "../api.js";
import { el, limpar } from "../dom.js";
import type { Tela } from "../tela.js";

/** Uma linha de "o que fazer", por código. O detalhe mora no runbook. */
const PRIMEIRO_PASSO: Record<string, string> = {
  worker_never_ran:
    "Confira se o processo subiu: pm2 describe nex-social-worker-<stage>.",
  worker_stale:
    "Veja o log antes de reiniciar — parado e quebrado chegam aqui iguais.",
  account_access_lost:
    "É do Business Manager, não daqui. Depois de resolver, rode o verify na aba Contas.",
  needs_human_review:
    "ABRA O PERFIL no Instagram antes de qualquer coisa. O post PODE estar no ar.",
  targets_failed:
    "Leia o last_error antes de reagendar — reagendar sem corrigir só gasta tentativa.",
  queue_not_draining:
    "O worker pode estar rodando e não publicando: conta inativa, cota ou acesso.",
  inactive_account_with_schedule:
    "Ou os agendamentos estão errados, ou a conta foi desligada sem querer.",
  credential_check_stale:
    "Rode o ciclo diário à mão: npm run daily. Ele não publica nada.",
  db_saturation:
    "A instância é compartilhada — pode não ser nosso. Veja o PROCESSLIST."
};

/** Os campos do retrato, em português e na ordem em que se lê. */
const ROTULOS: [string, string][] = [
  ["minutesSinceLastRun", "minutos desde o último ciclo"],
  ["runsInWindow", "ciclos na última hora"],
  ["published", "publicados na janela"],
  ["failed", "falhados na janela"],
  ["needsReview", "aguardando conferência humana"],
  ["accountsAborted", "interrupções por acesso"],
  ["overdueQueued", "vencidos ainda na fila"],
  ["inactiveAccountsWithWork", "contas desligadas com agendamento"],
  ["daysSinceCredentialCheck", "dias desde a checagem de credencial"],
  ["dbErrors", "erros de banco na janela"]
];

export function telaSaude(): Tela {
  return {
    titulo: "Saúde",
    async montar(raiz) {
      const painel = el("div");
      raiz.appendChild(painel);

      const carregar = async (): Promise<void> => {
        const { snapshot, alarms } = await Api.saude();
        limpar(painel);

        painel.appendChild(
          alarms.length === 0
            ? el(
                "div",
                { class: "aviso ok" },
                el("strong", {}, "Nada a reportar"),
                el(
                  "p",
                  {},
                  "Nenhum alarme. Vale lembrar que esta checagem roda DENTRO da máquina: " +
                    "ela pega processo morto e acesso perdido, não pega a máquina cair."
                )
              )
            : el("div", { class: "alarmes" }, ...alarms.map(cartaoAlarme))
        );

        const tabela = el("table", { class: "retrato" });
        for (const [chave, rotulo] of ROTULOS) {
          const v = snapshot[chave];
          tabela.appendChild(
            el(
              "tr",
              {},
              el("th", {}, rotulo),
              el(
                "td",
                { class: "mono" },
                v === null || v === undefined ? "—" : String(v)
              )
            )
          );
        }
        painel.appendChild(el("h3", {}, "O retrato"));
        painel.appendChild(tabela);

        const botao = el("button", { class: "secundario" }, "Atualizar");
        botao.addEventListener("click", () => void carregar());
        painel.appendChild(botao);
      };

      await carregar();
    }
  };
}

function cartaoAlarme(a: Alarme): HTMLElement {
  return el(
    "article",
    { class: `alarme ${a.level}` },
    el(
      "header",
      {},
      el(
        "span",
        { class: "nivel" },
        a.level === "critical" ? "CRÍTICO" : "aviso"
      ),
      el("code", {}, a.code)
    ),
    el("p", {}, a.message),
    PRIMEIRO_PASSO[a.code]
      ? el(
          "p",
          { class: "passo" },
          el("strong", {}, "Primeiro passo: "),
          PRIMEIRO_PASSO[a.code]!
        )
      : null
  );
}
